<script lang="ts" setup>
import { onMounted, onUpdated, ref, toRefs, watch } from 'vue';
import MultiSelect from 'primevue/multiselect';

import { AttendeeDropdownOptionInterface, ExtendedDisciplineInterface } from '@/types';
import { EMIT } from '@/enum';

interface Props {
  availableAttendees: AttendeeDropdownOptionInterface[];
  selectedAttendees: AttendeeDropdownOptionInterface[];
  groupTherapy: boolean;
  wrapperClass?: string;
  showIcons?: boolean;
  blur?: () => void;
  placeholder?: string;
}
const props = withDefaults(defineProps<Props>(), {
  wrapperClass: 'dashboard',
  showIcons: true,
  blur: undefined,
  placeholder: 'Select Attendees',
});
const emit = defineEmits<{ (e: EMIT.UPDATE, selected: AttendeeDropdownOptionInterface[]): void }>();
const attendeesDropdown = ref<InstanceType<typeof MultiSelect> | null>(null);
const multiAttendee = ref<AttendeeDropdownOptionInterface[]>([]);
const singleAttendee = ref<AttendeeDropdownOptionInterface[]>([]);
const selectionLimit = ref<number | undefined>(undefined);
const { availableAttendees, selectedAttendees, groupTherapy, blur, wrapperClass, showIcons } = toRefs(props);

const updateAttendees = (): void => {
  if (!groupTherapy.value) {
    emit(EMIT.UPDATE, singleAttendee.value as AttendeeDropdownOptionInterface[]);
    selectionLimit.value = 1;
  } else {
    emit(EMIT.UPDATE, multiAttendee.value as AttendeeDropdownOptionInterface[]);
    const sizeArray = multiAttendee.value.map((attendee) => {
      const groupSizeArray = attendee.disciplines.map((discipline) => discipline.group_size);
      return groupSizeArray.includes(null) ? null : Math.max(...(groupSizeArray.filter(Number) as number[]));
    });

    selectionLimit.value =
      !sizeArray.length || sizeArray.includes(null) ? undefined : Math.max(...(sizeArray.filter(Number) as number[]));
  }
};

const onBlurHandler = () => {
  if (blur.value !== undefined) blur.value();
};

const canBeInGroup = (disciplines: ExtendedDisciplineInterface[]): boolean => {
  const activeDisciplines = disciplines.filter((discipline) => discipline.is_active);
  return !activeDisciplines.every((discipline) => {
    return discipline.is_group === false;
  });
};

const setAttendees = () => {
  if (groupTherapy.value) {
    multiAttendee.value = selectedAttendees.value as AttendeeDropdownOptionInterface[];
  } else {
    singleAttendee.value = selectedAttendees.value as AttendeeDropdownOptionInterface[];
  }
};

watch(groupTherapy, setAttendees);
onMounted(() => {
  setAttendees();
});
onUpdated(() => {
  setAttendees();
});
</script>

<template>
  <div class="attendee-selector flex align-items-center" :class="wrapperClass" @blur="onBlurHandler">
    <div v-if="showIcons">
      <span
        v-if="Array.isArray(selectedAttendees) && selectedAttendees.length <= 1"
        class="material-icons group-icon material-symbols-outlined mr-3"
      >
        person
      </span>
      <span v-else class="material-icons group-icon material-symbols-outlined mr-3">group</span>
    </div>
    <MultiSelect
      v-if="groupTherapy"
      id="attendees"
      ref="attendeesDropdown"
      v-model="multiAttendee"
      class="w-full"
      panel-class="new-event-attendee-select"
      data-key="value"
      :options="availableAttendees"
      option-label="name"
      :filter="true"
      display="chip"
      :selection-limit="selectionLimit"
      :placeholder="placeholder"
      :show-toggle-all="false"
      @change="updateAttendees"
      @blur="onBlurHandler"
    >
      <template #option="attendee">
        <div class="inline-flex align-items-center w-full label-container">
          <span class="student-name">{{ attendee.option.name }}</span>
          <span v-if="canBeInGroup(attendee.option.disciplines)" class="material-icons ml-auto text-700">group</span>
        </div>
      </template>
    </MultiSelect>
    <MultiSelect
      v-else
      id="attendees"
      ref="attendeesDropdown"
      v-model="singleAttendee"
      class="w-full"
      panel-class="new-event-attendee-select"
      data-key="value"
      :options="availableAttendees"
      option-label="name"
      :filter="true"
      :placeholder="placeholder"
      :show-toggle-all="false"
      display="chip"
      :selection-limit="selectionLimit"
      @change="updateAttendees"
      @blur="onBlurHandler"
    />
  </div>
</template>

<style lang="scss" scoped>
.attendee-selector {
  .group-icon {
    color: #40b3dd;
  }
  .p-multiselect-label {
    .p-multiselect-token {
      border-radius: 0.25rem;
      background: $grayLight;
      transform: translateX(-0.25rem);

      .p-multiselect-token-label {
        line-height: 1rem;
      }

      .p-multiselect-token-icon {
        color: $grayMedium;
      }
    }
  }
  &.dashboard {
    :deep(.p-multiselect-label) {
      &.p-placeholder::before {
        font-family: 'Material Icons Round';
        margin-right: 0.25rem;
        content: 'search';
      }
    }
  }
}
</style>

<style lang="scss">
.new-event-attendee-select {
  .p-multiselect-items-wrapper {
    .p-multiselect-item {
      .label-container {
        display: flex;
        align-items: center;
        width: 100%;

        span:first-child {
          flex: 1;
        }

        span.material-icons {
          font-size: 1rem;
          color: $grayMedium;
        }
      }
      &.p-highlight {
        p {
          span.material-icons {
            color: $white;
          }
        }
      }
      &.p-disabled {
        color: $grayMedium;
      }
    }
  }
}
</style>
